import { FC, useContext, useMemo } from 'react';
import { useNavigation } from 'react-navi';
import { AuthContext } from '@repo-level/core-front';

import { useTheme } from 'hooks/theme';
import { BonanzaStatus } from 'helpers/bonanza';
import { BonanzaContext } from 'contexts/bonanza';
import { PERCENT_BREACKPOINTS_BONANZA } from 'modules/bonanza/components/BoxSection/helpers';
import { SmallProgressBar } from 'components/progressBars/SmallProgressBar';

import { ScBonanzaPromoSection } from './styled';
import { ProgressBarPalette } from 'components/progressBars/helpers';

export interface BonanzaPromoSectionProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const BonanzaPromoSection: FC<BonanzaPromoSectionProps> = ({
  isMobile = false,
  onLinkClick,
}) => {
  const {
    bonanzaReward,
    bonanzaData,
    bonanzaLevelItem,
    userBonanzaGroup,
  } = useContext(BonanzaContext);
  const { isAuthenticated } = useContext(AuthContext);

  const { navigate } = useNavigation();
  const { images } = useTheme();

  const { isLockedPromo, isBonusIssued, isHighlighted } = useMemo(
    () => ({
      isLockedPromo: userBonanzaGroup === BonanzaStatus.PROMO_LOCKED,
      isBonusIssued: userBonanzaGroup === BonanzaStatus.BONUS_ISSUED,
      isHighlighted: [
        BonanzaStatus.LEVEL3,
        BonanzaStatus.LEVEL4,
        BonanzaStatus.BONUS_ISSUED,
      ].includes(userBonanzaGroup),
    }),
    [userBonanzaGroup]
  );

  const imgSrc = useMemo(() => {
    if (!bonanzaLevelItem) return '';
    const { labelMob, label } = bonanzaLevelItem;
    if (isAuthenticated) {
      return isMobile ? labelMob?.url : label?.url;
    }
    return isMobile ? bonanzaData?.menu?.unloggedMob.url : label?.url;
  }, [isAuthenticated, isMobile, bonanzaLevelItem, bonanzaData]);

  const imgConfig = useMemo(
    () => ({
      src: imgSrc,
      width: isMobile ? 56 : 128,
      height: isMobile ? 56 : 32,
    }),
    [isMobile, imgSrc]
  );
  const progressbar = (
    <SmallProgressBar
      userProgress={bonanzaLevelItem?.progressPercent || 0}
      config={{
        dotsCount: PERCENT_BREACKPOINTS_BONANZA.length,
        color: ProgressBarPalette.PURPLE,
      }}
    />
  );

  const isPromoAvailable = isAuthenticated && !isLockedPromo && !isBonusIssued;
  if (!bonanzaData || !bonanzaLevelItem) return null;

  const {
    bg,
    yourProgress,
    yourReward,
    unLoggedLabelText,
    returnToStartFresh,
    openBonus,
  } = bonanzaData.menu;

  return (
    <ScBonanzaPromoSection
      onClick={() => {
        onLinkClick && onLinkClick();
        navigate('/bonanza');
      }}
      isMobile={isMobile}
      bgMenu={bg.url}
      isHighlighted={isHighlighted}
    >
      <div className="info">
        <div className="addons">
          {imgSrc && <img {...imgConfig} alt="logo" />}
          {isMobile && isPromoAvailable && (
            <div className="text">
              <span>
                <b>{yourProgress}:</b>
                {bonanzaLevelItem.progressPercent}%
              </span>
              <span>
                <b>{yourReward}:</b>
                {bonanzaReward}
              </span>
              {isPromoAvailable && isMobile && progressbar}
            </div>
          )}
          {!isAuthenticated ? (
            <p className="title">{unLoggedLabelText}</p>
          ) : isLockedPromo ? (
            <p className="title">{returnToStartFresh}</p>
          ) : isBonusIssued ? (
            <p className="title">{openBonus}</p>
          ) : null}
          {isMobile && <images.arrowRightSmallIcon.component />}
        </div>
      </div>

      {isPromoAvailable && !isMobile && (
        <>
          {progressbar}
          <p className="under-progress-text">
            {bonanzaLevelItem.progressPercent}%
          </p>
        </>
      )}
    </ScBonanzaPromoSection>
  );
};
